import ky from 'ky';
import type { KyInstance, Options } from 'ky';
import type {
  SvpAsset,
  SvpAssetAdditional,
} from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategory } from '@vgtv/api-client/lib/svp_category';
import dayjs from 'dayjs';
import type { CnpTag } from '@vgtv/api-client/lib/cnp_tag';
import type { UnixTimestamp } from '@vgtv/api-client';

import { config } from '../config';
import { SVP_ASSET_ADDITIONAL } from '../shared/constants';
import type { Vendor } from '../types';

import { createSvpApi } from './svp/apiClient';
import type {
  GetAssetArgs,
  GetAssetsArgs,
  GetMostSeenAssetsArgs,
  SearchAssetsArgs,
  GetSeasonAssetsArgs,
} from './svp/apiClient';
import api from './api';
import { SvpFilters } from './svp/filters';

const DEFAULT_ASSET_FILTERS = [
  'additional.settings.showInNewest<>false',
  'assetType::video',
];

function createCheckCategoryMatch(client: ReturnType<typeof createSvpApi>) {
  return async function checkCategoryMatch(
    {
      vendor,
      assetId,
      categoryId,
    }: {
      vendor: Vendor;
      assetId: SvpAsset['id'];
      categoryId: SvpCategory['id'];
    },
    options?: Options
  ): Promise<boolean> {
    const data = await client.getAssets(
      {
        vendor,
        filters: SvpFilters.from([
          `assetId::${assetId}`,
          `categoryId::${categoryId}`,
        ]),
      },
      options
    );

    return data._embedded.assets.length > 0;
  };
}

const checkLiveAssetsAvailability =
  (client: ReturnType<typeof createSvpApi>) =>
  async (
    {
      vendor,
      categoryId,
      tagId,
      startDate,
    }: {
      vendor: Vendor;
      categoryId?: SvpCategory['id'];
      tagId?: CnpTag['id'];
      startDate: UnixTimestamp;
    },
    options?: Options
  ) => {
    const response = await client.searchAssets(
      {
        vendor,
        limit: 1,
        filters: SvpFilters.from(
          [
            categoryId && `categoryId::${categoryId}`,
            tagId && `tagsid::${tagId}`,
            `streamType::live,wasLive`,
            `flightTimes.start>=${startDate}`,
          ].filter((s): s is string => Boolean(s))
        ),
      },
      options
    );

    return response.total_items > 0;
  };

interface StructureItem {
  id: string;
  type: string;
  label: string;
  items: StructureItem[];
}

const getLiveCategoryStructure =
  (client: ReturnType<typeof createSvpApi>) =>
  async (
    {
      vendor,
      categoryId,
      structure,
    }: {
      vendor: Vendor;
      categoryId: SvpCategory['id'];
      structure: StructureItem;
    },
    options?: Options
  ): Promise<StructureItem> => {
    const [categoryResponse, enrichmentResponse] = await Promise.all([
      client.getCategories(
        { vendor, filters: SvpFilters.from(`parentId::${categoryId}`) },
        options
      ),
      api.tag
        .getEnrichments({
          ...options,
          searchParams: {
            categoryId,
          },
        })
        .catch(() => []),
    ]);

    const categories = categoryResponse._embedded.categories.map(
      (category): StructureItem => {
        return {
          id: String(category.id),
          type: 'category',
          label: category.title,
          items: [],
        };
      }
    );

    const tags = enrichmentResponse.map((enrichment) => {
      return {
        id: enrichment.tag.id,
        type: 'tag',
        label: enrichment.tag.title,
        items: [],
      };
    });

    const startDate = dayjs().startOf('day').unix();

    structure.items = (
      await Promise.all([
        ...categories.map(async (category) => {
          const hasLiveAssets = await checkLiveAssetsAvailability(client)(
            {
              categoryId: Number(category.id) as SvpCategory['id'],
              startDate,
              vendor,
            },
            options
          );

          return hasLiveAssets
            ? getLiveCategoryStructure(client)(
                {
                  categoryId: Number(category.id) as SvpCategory['id'],
                  structure: category,
                  vendor,
                },
                options
              )
            : undefined;
        }),
        ...tags.map(async (tag) => {
          const hasLiveAssets = await checkLiveAssetsAvailability(client)(
            {
              tagId: tag.id,
              startDate,
              vendor,
            },
            options
          );

          return hasLiveAssets ? tag : undefined;
        }),
      ])
    ).filter((item): item is StructureItem => Boolean(item));

    return structure;
  };

export function createSvpApiClient(ky: KyInstance, appName: string) {
  const apiClient = createSvpApi(ky, appName);
  return {
    getAsset: (args: Omit<GetAssetArgs, 'additional'>, options?: Options) =>
      apiClient.getAsset<SvpAssetAdditional>(
        {
          ...args,
          additional: SVP_ASSET_ADDITIONAL,
        },
        options
      ),
    checkCategoryMatch: createCheckCategoryMatch(apiClient),
    getAssets: (args: Omit<GetAssetsArgs, 'additional'>, options?: Options) =>
      apiClient.getAssets<SvpAssetAdditional>(
        {
          ...args,
          additional: SVP_ASSET_ADDITIONAL,
          filters: SvpFilters.from(args.filters).concat(DEFAULT_ASSET_FILTERS),
        },
        options
      ),
    searchAssets: (
      args: Omit<SearchAssetsArgs, 'additional'>,
      options?: Options
    ) =>
      apiClient.searchAssets<SvpAssetAdditional>(
        {
          ...args,
          additional: SVP_ASSET_ADDITIONAL,
          filters: SvpFilters.from(args.filters).concat(DEFAULT_ASSET_FILTERS),
        },
        options
      ),
    getMostSeenAssets: (
      args: Omit<GetMostSeenAssetsArgs, 'additional'>,
      options?: Options
    ) =>
      apiClient.getMostSeenAssets<SvpAssetAdditional>(
        {
          ...args,
          additional: SVP_ASSET_ADDITIONAL,
          filters: SvpFilters.from(args.filters).concat(DEFAULT_ASSET_FILTERS),
        },
        options
      ),

    getCategory: apiClient.getCategory,
    getCategories: apiClient.getCategories,
    getLiveCategoryStructure: getLiveCategoryStructure(apiClient),

    getSeasonAssets: (
      args: Omit<GetSeasonAssetsArgs, 'additional'>,
      options?: Options
    ) =>
      apiClient.getSeasonAssets<SvpAssetAdditional>(
        {
          ...args,
          additional: SVP_ASSET_ADDITIONAL,
        },
        options
      ),
  };
}

export default createSvpApiClient(
  ky.create({
    prefixUrl: config.svpApiHostname,
    retry: 3,
  }),
  config.appName
);
